/**
 * 分数の**書きかたの読み取り**(表示だけのための純関数)。
 *
 * 問題文・選択肢・解説・盤面の値には、分数が `3/4` の1行書きで入っている
 * (データの原本は `tools/content-build/data/curated/`。生成器も配信JSONもこの形)。
 * 算数の教科書は分数を**縦**(分子・横線・分母)に組むので、画面もそう出したい。
 *
 * ここは「文字列のどこが分数か」を切り出すだけで、**描画も判定もしない**:
 *   - HTML 側(`ui/learn/FractionText.tsx`)は縦積みの `<span>` に組む
 *   - SVG 側(盤面。`lesson.ts` の `fitLessonFractionText`)は 2段+横線に組む
 * データ・読み上げ・正誤判定は一切変えない(`3/4` は `3/4` のまま)。
 *
 * **かな表記(「5ぶんの7」)は対象外**。あれは読み言葉としてそのまま出す
 * (低学年の帯は分数を式で書かせない。docs/13。無理に縦へ組むと逆に読めなくなる)。
 */

/** 分数ではない ふつうの文字の並び。 */
export interface FractionTextRun {
  readonly kind: 'text';
  readonly text: string;
}

/**
 * 分数ひとつ。`text` は元の書きかた(読み上げ・コピーはこちらを使う)。
 *
 * `whole` が入っているのは**帯分数**(「1と3/4」)。整数部と分数は1つの数なので、
 * 描画側は途中で折り返さず・離さずに並べる。
 */
export interface FractionRun {
  readonly kind: 'fraction';
  readonly text: string;
  /** 帯分数の整数部(「1と3/4」の `1`)。ふつうの分数では無い。 */
  readonly whole?: string;
  /** 分子(うえ)。 */
  readonly numerator: string;
  /** 分母(した)。 */
  readonly denominator: string;
}

/** `parseFractionRuns` の返り値。 */
export type FractionSegment = FractionTextRun | FractionRun;

/**
 * 分数の候補。`12と3/4`(帯分数)を先に見るので、`12` だけが整数部に落ちる。
 *
 * 桁数を3桁までに絞ってあるのは、`2024/2025` のような**年号やページ番号を
 * 分数にしてしまわない**ため(算数の分母はせいぜい2桁)。
 */
const FRACTION_PATTERN = /(\d{1,3})と(\d{1,3})\/(\d{1,3})|(\d{1,3})\/(\d{1,3})/g;

/**
 * 分数の前後に来てはいけない字。
 *
 * `.` … `1.5/2` のような小数の途中を切らない
 * `/` … `2024/5/6` のような日付を分数にしない
 * 数字 … 桁数の上限で切れた残り(`1234/5` の `234/5`)を拾わない
 */
function isFractionBoundaryBlocker(ch: string | undefined): boolean {
  return ch !== undefined && (ch === '.' || ch === '/' || (ch >= '0' && ch <= '9'));
}

/**
 * 文字列を「ふつうの文字」と「分数」に分ける。
 *
 * 分数と見なすのは `整数/整数` で、**前後が数字・小数点・スラッシュでない**もの。
 * 直前が `整数と` なら帯分数として整数部も一緒に持つ(「1と3/4」)。
 *
 * 分数が1つも無ければ、要素1つ(`text`)の配列を返す(空文字なら空配列)。
 * 元の文字列は連結すれば必ず復元できる(表示だけを変える約束)。
 */
export function parseFractionRuns(text: string): readonly FractionSegment[] {
  if (text === '') return [];
  const out: FractionSegment[] = [];
  let last = 0;

  // 走査のたびに状態を持ち越さないよう、正規表現はここで作り直す。
  const pattern = new RegExp(FRACTION_PATTERN.source, 'g');
  let match = pattern.exec(text);
  while (match !== null) {
    const [raw, mixedWhole, mixedNum, mixedDen, plainNum, plainDen] = match;
    const start = match.index;
    const end = start + raw.length;
    const before = start > 0 ? text[start - 1] : undefined;
    const after = end < text.length ? text[end] : undefined;

    if (!isFractionBoundaryBlocker(before) && !isFractionBoundaryBlocker(after)) {
      if (start > last) out.push({ kind: 'text', text: text.slice(last, start) });
      // どちらの書きかたで当たったかは、取れた捕獲グループで分かる。
      if (mixedWhole !== undefined && mixedNum !== undefined && mixedDen !== undefined) {
        out.push({
          kind: 'fraction',
          text: raw,
          whole: mixedWhole,
          numerator: mixedNum,
          denominator: mixedDen,
        });
      } else {
        out.push({
          kind: 'fraction',
          text: raw,
          numerator: plainNum ?? '',
          denominator: plainDen ?? '',
        });
      }
      last = end;
    }

    // 弾いたときは**1文字だけ**ずらして探し直す。まるごと飛ばすと、
    // `1234と3/4` の中の `3/4` まで巻き添えで見落とす。
    pattern.lastIndex = last === end ? end : start + 1;
    match = pattern.exec(text);
  }

  if (last < text.length) out.push({ kind: 'text', text: text.slice(last) });
  return out;
}

/** 分数が入っているか(縦に組む必要があるか)。 */
export function hasFraction(text: string): boolean {
  return parseFractionRuns(text).some((run) => run.kind === 'fraction');
}
